import { RunFlowTaskInfo } from '~src/flow/models/RunFlowTaskInfo';
import { FlowWithRunPermission, GetFlowsWithRunPermissionsRequest } from '~src/flow/serverApi/requests/GetFlowsWithRunPermissionsRequest';
import { SubscriptionFlow } from '~src/flow/serverApi/types/SubscriptionFlow';
import { CreateSubscriptionsRequest } from '~src/subscription/serverApi/requests/CreateSubscriptionsRequest';

import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { removeDraftIdPrefix } from '../../drafts/utils/DraftUtils';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../../recents/requests/MarkRecentlyViewedRequest';
import { IDetailFetcher } from '../../ts/fetchers/IDetailFetcher';
import { IListFetcher } from '../../ts/fetchers/IListFetcher';
import { ServerFilterClauses } from '../../ts/helpers/ServerFilterClauses';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { ITaggableContent } from '../../ts/models/ITaggableContent';
import { FlowDraftInfo } from '../../ts/models/resourceInfoParts';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { PerformExtractEncryptionActionOnFlowDraftsRequest } from '../../ts/ServerApi/requests/PerformExtractEncryptionActionOnFlowDraftsRequest';
import { PerformExtractEncryptionActionOnFlowsRequest } from '../../ts/ServerApi/requests/PerformExtractEncryptionActionOnFlowsRequest';
import { UnencryptedConnectionKeychain } from '../../ts/ServerApi/types/IConnectionKeyChain';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { FlowBaseInfo, FlowBasicInfo, FlowDetailInfo, FlowListItemInfo } from '../models/FlowInfo';
import { FlowOutputStepInfo } from '../models/FlowOutputStepInfo';
import { FlowMapper } from '../models/mappers/FlowMapper';
import { AddTagsToFlowsRequest } from '../serverApi/requests/AddTagsToFlowsRequest';
import { CreateRunFlowTaskRequest } from '../serverApi/requests/CreateRunFlowTaskRequest';
import { DeleteFlowDraftsRequest } from '../serverApi/requests/DeleteFlowDraftsRequest';
import { DeleteFlowsRequest } from '../serverApi/requests/DeleteFlowsRequest';
import { GetFlowActionsRequest } from '../serverApi/requests/GetFlowActionsRequest';
import { GetFlowDraftActionsRequest } from '../serverApi/requests/GetFlowDraftActionsRequest';
import { GetFlowDraftRequest } from '../serverApi/requests/GetFlowDraftRequest';
import { GetFlowInputStepsRequest } from '../serverApi/requests/GetFlowInputStepsRequest';
import { GetFlowOutputStepsRequest } from '../serverApi/requests/GetFlowOutputStepsRequest';
import { GetFlowsRequest } from '../serverApi/requests/GetFlowsRequest';
import { MoveFlowDraftsToProjectRequest } from '../serverApi/requests/MoveFlowDraftsToProjectRequest';
import { MoveFlowsToProjectRequest } from '../serverApi/requests/MoveFlowsToProjectRequest';
import { RemoveTagsFromFlowsRequest } from '../serverApi/requests/RemoveTagsFromFlowsRequest';
import { RevertFlowRequest, RevertFlowResult } from '../serverApi/requests/RevertFlowRequest';
import { RunFlowOnOutputStepsRequest } from '../serverApi/requests/RunFlowsOnOutputStepsRequest';
import { RunFlowsRequest } from '../serverApi/requests/RunFlowsRequest';
import { SetFlowDescriptionRequest } from '../serverApi/requests/SetFlowDescriptionRequest';
import { SetFlowDraftNameRequest } from '../serverApi/requests/SetFlowDraftNameRequest';
import { SetFlowNameRequest } from '../serverApi/requests/SetFlowNameRequest';
import { SetFlowsOwnerRequest } from '../serverApi/requests/SetFlowsOwnerRequest';
import { FlowAllowedActions } from '../serverApi/types/FlowAllowedActions';
import { FlowDraftAllowedActions } from '../serverApi/types/FlowDraftAllowedActions';
import { FlowInputStep } from '../serverApi/types/FlowInputStep';
import { FlowOutputStep } from '../serverApi/types/FlowOutputStep';
import { IFlowRunSpecOutputStepViewModel } from '../serverApi/types/IFlowRunSpecOutputStepViewModel';
import { FlowParameterParams,RunFlowParams } from '../types/FlowParameters';

export class FlowsResource implements IListResource<FlowListItemInfo>, IFieldValuesResource, IDetailResource<FlowDetailInfo>, IActionResource<FlowAllowedActions>, ITaggableContent {

  private listFetcher: IListFetcher<FlowListItemInfo>;
  private listWithPermissionsFetcher: IListFetcher<FlowWithRunPermission>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;
  private detailFetcher: IDetailFetcher<FlowDetailInfo>;

  constructor(
  ) {
    this.listFetcher = new WebClientListResourceFetcher(GetFlowsRequest, ServerApiMapper.flows);
    this.listWithPermissionsFetcher = new WebClientListResourceFetcher(GetFlowsWithRunPermissionsRequest, ServerApiMapper.flowsWithPermissions);
    this.detailFetcher = new WebClientDetailFetcher('getFlow', FlowMapper.mapDetail);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<FlowListItemInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public allFlowsWithPermission(): ResourceQuery<FlowWithRunPermission> {
    return new ResourceQuery(this.listWithPermissionsFetcher);
  }

  public actions(id: string): Promise<FlowAllowedActions> {
    return WebHttpClient.sendRequest(new GetFlowActionsRequest(id));
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToFlowsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromFlowsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public draftActions(draftId: string): Promise<FlowDraftAllowedActions> {
    return WebHttpClient.sendRequest(new GetFlowDraftActionsRequest(removeDraftIdPrefix(draftId)));
  }

  public renameDraft(draftId: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetFlowDraftNameRequest({ id: removeDraftIdPrefix(draftId), name: name }));
  }

  public moveDraftsToProject(targetProjectId: string, draftIds: string[]): Promise<IResult> {
    const draftIdsWithoutPrefix = draftIds.map(id => {
      return removeDraftIdPrefix(id);
    });
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(draftIdsWithoutPrefix, (idsBlock) => new MoveFlowDraftsToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd));
  }

  public deleteFlowDrafts(draftIds: string[]): Promise<IResult> {
    const draftIdsWithoutPrefix = draftIds.map(id => {
      return removeDraftIdPrefix(id);
    });
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(draftIdsWithoutPrefix, (idsBlock) => new DeleteFlowDraftsRequest(idsBlock)),
      ResultHelper.combineResults
    );
  }

  public moveToProject(targetProjectId: string, flowIds: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(flowIds, (idsBlock) => new MoveFlowsToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public getById(id: string): Promise<FlowDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public getDraft(draftId: string): Promise<FlowDraftInfo> {
    return WebHttpClient.sendRequest(new GetFlowDraftRequest({ id: removeDraftIdPrefix(draftId) }));
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'flow');
  }

  public getFlowsByDatasourceId(datasourceId: string): Promise<FlowListItemInfo[]> {
    const query = this.all().addFilter(ServerFilterClauses.serverFilterClauses.datasource(datasourceId));
    return SliceableHelper.fetchAll(query).then(listResult => listResult.items);
  }

  public getInputSteps(flowId: string): Promise<ReadonlyArray<FlowInputStep>> {
    return WebHttpClient.sendRequest(new GetFlowInputStepsRequest({ flowId }))
      .then((result) => result.inputSteps || []); // what to do in the case of an error?
  }

  public getOutputSteps(flowId: string): Promise<ReadonlyArray<FlowOutputStep>> {
    return WebHttpClient.sendRequest(new GetFlowOutputStepsRequest({ flowId }))
      .then((result) => result.outputSteps || []); // what to do in the case of an error?
  }

  public getOutputStepInfos(flowId: string): Promise<ReadonlyArray<FlowOutputStepInfo>> {
    return WebHttpClient.sendRequest(new GetFlowOutputStepsRequest({ flowId }))
      .then((result) => ServerApiMapper.flowOutputSteps(result));
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetFlowNameRequest({ id: id, name: name }));
  }

  public runFlow(ids: string[], isRunAllIncremental: boolean, runFlowParams?: RunFlowParams[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunFlowsRequest(idsBlock, isRunAllIncremental, runFlowParams)),
      ResultHelper.combineResults
    );
  }

  public runFlowOnOutputSteps(flowId: string, flowOutputStepIds: string[], isIncremental: boolean, parameterParams?: FlowParameterParams[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new RunFlowOnOutputStepsRequest({
        flowId: flowId,
        flowOutputStepIds: flowOutputStepIds,
        flowRunSpecOutputStepParams: flowOutputStepIds.map(id => ({ isIncremental: isIncremental, flowOutputStepId: parseInt(id, 10) })),
        parameterParams: parameterParams,
      }));
  }

  public setDescription(id: string, description: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new SetFlowDescriptionRequest(id, description));
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetFlowsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public createSubscriptionOnRunFlowTask(subscriptionFlow: SubscriptionFlow, runFlowTask: RunFlowTaskInfo): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new CreateSubscriptionsRequest({
        scheduleId: runFlowTask.schedule.id,
        subject: subscriptionFlow.subscriptionFlowMetadata.subject,
        userIds: subscriptionFlow.subscriptionFlowMetadata.userIds,
        groupIds: subscriptionFlow.subscriptionFlowDetails.groupIds,
        targetType: 'FLOW',
        targetId: subscriptionFlow.subscriptionFlowMetadata.targetId,
        subscriptionMessage: subscriptionFlow.subscriptionFlowMetadata.message,
        subscriptionFlowDetails: subscriptionFlow.subscriptionFlowDetails,
      })
    );
  }

  public createRunFlowTask(
    id: string,
    scheduleId: string,
    runAllOutputs: boolean,
    isRunAllIncremental: boolean,
    flowOutputStepIds: string[],
    flowRunSpecOutputStepParams: IFlowRunSpecOutputStepViewModel[],
    sendEmailWhenDone: boolean,
    subscriptionFlow?: SubscriptionFlow,
    parameterParams?: FlowParameterParams[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new CreateRunFlowTaskRequest({
        scheduleId: scheduleId,
        flowId: id,
        runAllOutputs: runAllOutputs,
        isRunAllIncremental: isRunAllIncremental,
        flowOutputStepIds: flowOutputStepIds,
        flowRunSpecOutputStepParams: flowRunSpecOutputStepParams,
        sendEmailWhenDone: sendEmailWhenDone,
        subscriptionFlow: subscriptionFlow !== null && sendEmailWhenDone ? subscriptionFlow : null,
        parameterParams: parameterParams,
      }));
  }

  public revertFlow(id: string, version: number, keychains?: UnencryptedConnectionKeychain[], encryptedPasswordKeyId?: string): Promise<RevertFlowResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new RevertFlowRequest({
      id,
      version,
      keychains,
      encryptedPasswordKeyId
    }));
  }

  public deleteFlows(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteFlowsRequest(idsBlock)),
      ResultHelper.combineResults
    );
  }

  public encryptFlows(ids: string[], actionType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new PerformExtractEncryptionActionOnFlowsRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults);
  }

  // Encrypt unparented flow draft
  public encryptFlowDrafts(ids: string[], actionType: string): Promise<IResult> {
    const draftIdsWithoutPrefix = ids.map(id => {
      return removeDraftIdPrefix(id);
    });
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(draftIdsWithoutPrefix, (idsBlock) =>
        new PerformExtractEncryptionActionOnFlowDraftsRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults
    );
  }

  public hasDataQualityWarning(contentItem: FlowBaseInfo): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators[0]
      && contentItem.dataQualityIndicators[0].isActive;
  }

  public isFlowInfo(item: any): item is FlowBasicInfo {
    return item &&
      item.contentType &&
      item.contentType === 'flow' &&
      item.hasOwnProperty('project');
  }

  public markRecentlyViewed(id: string) {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.Flow
    }));
  }
}

export const Flows = new FlowsResource();
